// AdminVaultPage — /admin/vault — vault management for admins.
// Backend enforces is_admin on every endpoint; this UI just surfaces it.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, PlayCircle, Loader2 } from "lucide-react";
import { API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const fmt = (n) => Number(n).toLocaleString();

export default function AdminVaultPage() {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    if (!token) return;
    fetch(`${API}/vault/admin/overview`, { credentials: "include", headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 403 ? "Solo admins" : "Error cargando");
        setData(await r.json());
      })
      .catch((e) => setError(e.message));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const distribute = async () => {
    if (!window.confirm("¿Ejecutar la distribución mensual? Enviará R₿L REALES desde el treasury a todos los depositantes.")) return;
    setRunning(true); setResult(null);
    try {
      const r = await fetch(`${API}/vault/distribute`, {
        method: "POST", credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.detail?.error || "distribute_failed");
      setResult(d);
      load();
    } catch (e) {
      setResult({ error: e.message });
    } finally {
      setRunning(false);
    }
  };

  if (!user) return null;
  if (error) return <div className="min-h-screen bg-[#05060B] pt-32 text-center text-red-400">{error}</div>;

  const s = data?.stats;
  return (
    <div className="min-h-screen bg-[#05060B] text-white" data-testid="admin-vault-page">
      <section className="mx-auto max-w-5xl px-6 pt-32 pb-24">
        <Link to="/vault" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-white/40 hover:text-amber-300">
          <ArrowLeft className="h-3.5 w-3.5" /> Vault
        </Link>
        <h1 className="mt-4 text-3xl font-black">Admin · R₿L Vault</h1>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            ["TVL", s ? `${fmt(s.tvl_rbl)} R₿L` : "…"],
            ["Pool pendiente", s ? `${fmt(s.pool_pending_rbl)} R₿L` : "…"],
            ["Depositantes", s ? fmt(s.depositors) : "…"],
            ["APY est.", s?.estimated_apy != null ? `~${s.estimated_apy}%` : "—"],
          ].map(([l, v]) => (
            <div key={l} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">{l}</p>
              <p className="mt-1 font-mono text-xl font-black">{v}</p>
            </div>
          ))}
        </div>

        {/* Distribute */}
        <div className="mt-6 rounded-2xl border border-amber-400/30 bg-amber-400/[0.05] p-5">
          <button onClick={distribute} disabled={running || !s || s.pool_pending_rbl <= 0}
            data-testid="admin-vault-distribute-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-6 py-3 text-xs font-black uppercase tracking-[0.2em] text-black disabled:opacity-40">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
            Ejecutar distribución mensual
          </button>
          {result && (
            <p className={`mt-3 text-xs ${result.error ? "text-red-400" : "text-emerald-300"}`} data-testid="admin-vault-distribute-result">
              {result.error
                ? `Error: ${result.error}`
                : `✓ ${result.period}: ${fmt(result.total_yield_rbl)} R₿L · enviados ${result.sent} · fallidos ${result.failed}`}
            </p>
          )}
        </div>

        {/* Active deposits */}
        <h2 className="mt-8 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-white/45">
          Depósitos activos ({data?.deposits?.length ?? 0})
        </h2>
        <div className="mt-3 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02]">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-[0.18em] text-white/35">
                <th className="px-4 py-3">Wallet</th>
                <th className="px-4 py-3">R₿L</th>
                <th className="px-4 py-3">Depositado</th>
                <th className="px-4 py-3">Elegible (sin fee)</th>
              </tr>
            </thead>
            <tbody>
              {(data?.deposits || []).map((d, i) => (
                <tr key={i} className="border-b border-white/5 text-white/75">
                  <td className="px-4 py-3 font-mono">{d.wallet_address.slice(0, 8)}…{d.wallet_address.slice(-6)}</td>
                  <td className="px-4 py-3 font-mono text-amber-300">{fmt(d.amount_rbl)}</td>
                  <td className="px-4 py-3">{d.deposited_at.slice(0, 10)}</td>
                  <td className="px-4 py-3">{d.eligible_at.slice(0, 10)}</td>
                </tr>
              ))}
              {!data?.deposits?.length && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-white/35">Sin depósitos</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Past distributions */}
        <h2 className="mt-8 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-white/45">
          Distribuciones ejecutadas
        </h2>
        <div className="mt-3 space-y-2">
          {(data?.distributions || []).map((d) => (
            <div key={d.period} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-xs">
              <span className="font-mono font-bold">{d.period}</span>
              <span className="font-mono text-amber-300">{fmt(d.total_yield_rbl)} R₿L</span>
              <span className="text-white/45">{d.depositor_count} depositantes</span>
              <span className="text-white/35">{d.distributed_at.slice(0, 10)}</span>
            </div>
          ))}
          {!data?.distributions?.length && <p className="text-xs text-white/35">Ninguna todavía</p>}
        </div>
      </section>
    </div>
  );
}
